import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useAccountReadContext, useAccountWriteContext } from '@context/AccountContextContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Body1, Heading6 } from '@ui/Typography'

import { isAccountProofOfIdentificationVerified } from '@utils/AccountAccess/accountCompanyStatuses'
import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { CommonPaths } from '@utils/path'

import type { WalletDepositFormValues } from '@model/WalletDepositFormValues'

import { useDocumentVerificationPath } from '@hooks/useDocumentVerificationPath'
import { useFormatNumber } from '@hooks/useFormatNumber'

import { Loading } from '@shared/components/Loading/Loading'

import { FirstTimeDepositForm } from '../../Signup/Pages/FirstTimeDeposit/Components/FirstTimeDepositForm'
import { useDepositSubmit } from '../../Signup/Pages/FirstTimeDeposit/hooks/useDepositSubmit'
import { useGetDepositCurrencyLimits } from '../../Traders-Room/Wallets/WalletDeposit/WalletDepositForm/hooks/useGetDepositCurrencyLimits'
import { useWalletDepositMutate } from '../../Traders-Room/Wallets/WalletDeposit/WalletDepositPage/WalletDepositPage'

import styles from './FirstTimeDepositPage.module.scss'

export const FirstTimeDepositPage: React.FC = () => {
  const { account } = useAccountReadContext()
  const docVerificationPath = useDocumentVerificationPath()

  const skipRedirectPath = isAccountProofOfIdentificationVerified(account)
    ? CommonPaths.TradersRoomWallets
    : docVerificationPath

  const location = useLocation()
  const apiClient = useApiClient(ClientApiClient)
  const { formatMoney } = useFormatNumber()
  const { t } = useTranslation()
  const { refreshAccount } = useAccountWriteContext()
  const locale = useSessionLanguage()
  const { handleDepositSubmit } = useDepositSubmit()
  const { getDepositCurrencyLimits } = useGetDepositCurrencyLimits()

  const { data: wallets, isLoading: isLoadingWallets } = useQuery({
    queryKey: queryKeys.profile.wallets(),
    queryFn: () => {
      if (location?.state?.wallets?.length) {
        return location.state.wallets
      }
      return apiClient.getWallets()
    },
    refetchOnMount: 'always',
  })

  const walletDepositMutate = useWalletDepositMutate({
    onSubmit: (data, values) => {
      handleDepositSubmit(data, values)
      refreshAccount(locale)
    },
  })

  const handleFormSubmit = async (values: WalletDepositFormValues) => {
    walletDepositMutate.mutate(values)
  }

  return (
    <section className={styles.firstTimeDepositPage}>
      <div className={styles.header}>
        <Heading6 medium>{t('Fund your Wallet')}</Heading6>

        <Body1 isParagraph className={styles.description}>
          {t(
            'Profile.Secure your account by making your first deposit. Choose your preferred payment method to get started!'
          )}
        </Body1>
      </div>
      <Loading showLoadingIcon isLoading={isLoadingWallets}>
        <FirstTimeDepositForm
          wallets={wallets ?? []}
          onSubmit={handleFormSubmit}
          getDepositCurrencyLimits={getDepositCurrencyLimits}
          formatMoney={formatMoney}
          skipButtonLink={skipRedirectPath}
        />
      </Loading>
    </section>
  )
}
